/**
 * View-model mapping + filter/sort helpers for the Templates tab of the process gallery.
 *
 * The catalog itself is backend content: `WorkFlowService.GetProcessTemplates()` reads
 * `GET /v1/api/workflow/templates`, which serves Metadatas/process-templates.json
 * verbatim — so templates are editable on the backend without a designer redeploy.
 * The Recently modified / All Processes tabs run on real `IWorkFlowDto` rows
 * (see create-workflow-processes.ts).
 */

import type { IProcessTemplateDto } from "@kuika/kuika-cl-model/src/dtos/workflow.dto";
import type { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";

export interface IProcessTemplate {
  id: string;
  name: string;
  /** Seed for the created process's name (satisfies the workflow-name rule). */
  defaultProcessName: string;
  tag: string;
  /** Derived from `json` (never author-maintained), so it cannot drift from the diagram. */
  stepCount: number;
  /** One-line summary of the flow, shown in the template-details modal. */
  description: string;
  /** The nodes model the created process starts with, as stored on IWorkFlowDto.json. */
  json: string;
  /** The BPMN XML the created process (and the card preview) starts with. */
  xml: string;
}

/** Minimal slice of the nodes model needed to count a catalog entry's steps. */
interface ITemplateNodesJson {
  nodes?: { steps?: unknown[] };
}

/**
 * Counts a template's steps from its own json — the single source of truth — so the
 * "N steps" metadata can never contradict the diagram (the exact drift the old
 * hand-maintained stepCount field allowed). Malformed json degrades to 0 rather than
 * throwing: the catalog is hand-editable backend content.
 */
export const countTemplateSteps = (json: string): number => {
  try {
    const parsed: ITemplateNodesJson = JSON.parse(json) as ITemplateNodesJson;
    return Array.isArray(parsed.nodes?.steps) ? parsed.nodes.steps.length : 0;
  } catch {
    return 0;
  }
};

/** DTO → view-model. Entries whose json/xml is missing are dropped: nothing could instantiate them. */
export const toProcessTemplates = (dtos: readonly IProcessTemplateDto[]): IProcessTemplate[] =>
  dtos
    .filter((dto: IProcessTemplateDto): boolean => !!dto.xml && !!dto.json)
    .map((dto: IProcessTemplateDto): IProcessTemplate => ({
      id: dto.id,
      name: dto.name,
      defaultProcessName: dto.defaultProcessName,
      tag: dto.tag ?? "",
      stepCount: countTemplateSteps(dto.json),
      description: dto.description ?? "",
      json: dto.json,
      xml: dto.xml
    }));

export type TemplateSortKey = "name-asc" | "name-desc" | "steps-desc" | "steps-asc";

export interface ITemplateSortOption {
  value: TemplateSortKey;
  label: string;
}

export const TEMPLATE_SORT_OPTIONS: ITemplateSortOption[] = [
  { value: "name-asc", label: "Name (A–Z)" },
  { value: "name-desc", label: "Name (Z–A)" },
  { value: "steps-asc", label: "Fewest steps" },
  { value: "steps-desc", label: "Most steps" }
];

/** The content pair a new process is created with. */
export interface IProcessContent {
  json: string;
  xml: string;
}

const escapeXmlAttribute = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * A template's diagram labels its pool/process with the template's DISPLAY name; the
 * created process gets its own name, so the labels are rewritten to match. Best-effort
 * by design: the catalog is hand-editable backend content, so an entry whose XML does
 * not carry the expected `name="…"` attributes (or whose json does not parse) is used
 * verbatim rather than rejected — a stale pool label is cosmetic, a failed create is not.
 */
export const personalizeTemplateContent = (template: IProcessTemplate, processName: string): IProcessContent => {
  const oldAttribute: string = `name="${escapeXmlAttribute(template.name)}"`;
  const newAttribute: string = `name="${escapeXmlAttribute(processName)}"`;
  const xml: string = template.xml.split(oldAttribute).join(newAttribute);

  let json: string = template.json;
  try {
    const parsed: { nodes?: { description?: string } } = JSON.parse(template.json) as {
      nodes?: { description?: string };
    };
    if (parsed.nodes && typeof parsed.nodes.description === "string") {
      parsed.nodes.description = processName;
      json = JSON.stringify(parsed);
    }
  } catch {
    // Malformed json fails loudly later on the create path; don't mask it here.
  }
  return { json, xml };
};

interface ITaggedProcessItem {
  tag: string;
}

interface ISortableProcessItem {
  name: string;
  stepCount: number;
}

/**
 * Diacritic- and case-insensitive search key. Locale-INDEPENDENT on purpose:
 * `toLowerCase()` (never `toLocaleLowerCase()`) then strip combining marks, so
 * "İzin" → "izin" and "Süreci" → "sureci". Without the NFD pass, lowercasing "İ"
 * yields "i" + U+0307 and a user typing "izin" would match nothing.
 */
const normalizeForSearch = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const collectProcessTags = (items: readonly ITaggedProcessItem[]): string[] => {
  const seen: Set<string> = new Set<string>();
  items.forEach((item: ITaggedProcessItem): void => {
    if (item.tag) seen.add(item.tag);
  });
  return Array.from(seen).sort((a: string, b: string): number => a.localeCompare(b, "en", { sensitivity: "base" }));
};

/**
 * Badge colour a template's tag reads with, so the same tag is the same colour in the
 * card and in the details modal. Mapped explicitly (not hashed) because the design
 * pins these three: HR is blue, Finance purple, Procurement orange. Unknown tags fall
 * back to the neutral chip rather than borrowing a meaning-carrying colour.
 */
export const getTemplateTagColor = (tag: string): KuikaBadgeColor => {
  if (tag === "HR") return "blue";
  if (tag === "Finance") return "purple";
  if (tag === "Procurement") return "orange";
  if (tag === "IT") return "green";
  return "grey";
};

const matchesQuery = (name: string, tag: string, query: string): boolean => {
  const trimmed: string = query.trim();
  if (!trimmed) return true;
  const needle: string = normalizeForSearch(trimmed);
  return normalizeForSearch(name).includes(needle) || normalizeForSearch(tag).includes(needle);
};

/**
 * Explicit "en" locale so the order is identical on a Turkish, German, or English
 * machine — a system-locale-dependent sort would make the gallery non-deterministic.
 */
const compareByName = (a: ISortableProcessItem, b: ISortableProcessItem): number =>
  a.name.localeCompare(b.name, "en", { sensitivity: "base" });

/**
 * `sort === null` means "no explicit sort" — the catalog's own order is kept. The
 * gallery opens in that state (the Sort by control shows its placeholder), which is
 * what the design specifies.
 */
export const filterProcessTemplates = (
  templates: readonly IProcessTemplate[],
  query: string,
  tag: string | null,
  sort: TemplateSortKey | null
): IProcessTemplate[] => {
  const filtered: IProcessTemplate[] = templates.filter(
    (template: IProcessTemplate): boolean =>
      matchesQuery(template.name, template.tag, query) && (!tag || template.tag === tag)
  );
  if (!sort) return filtered;

  return [...filtered].sort((a: IProcessTemplate, b: IProcessTemplate): number => {
    if (sort === "name-desc") return compareByName(b, a);
    if (sort === "steps-desc") return b.stepCount - a.stepCount || compareByName(a, b);
    if (sort === "steps-asc") return a.stepCount - b.stepCount || compareByName(a, b);
    return compareByName(a, b);
  });
};
